import * as React from "react";
import { useEffect, useCallback, useState } from "react";
import { LinkCopier } from "../../../../molecules/copier";
import {
  SmartApplyApiError,
  SurveyEntity,
  SurveySearchEngineSetting,
  SurveyNotifyArrivalSetting,
  isLGWANURL,
  reportUnexpectedError,
  surveyApiClient,
} from "@graffer-inc/smart-apply";
import { SecondaryButton, TextField } from "@graffer-inc/console-design-kit";
import { Group } from "../../../../../application/domain/group/group";
import { useAuthContextV2 } from "../../../../../auth-state";
import { useModal } from "../../../../../utils/custom-hooks";
import { OverwriteCustomPathModal } from "../modal/overwrite-custom-path-modal";
import { toast } from "react-toastify";
import { Loading } from "../../../../organisms/common/loading";
import { StaticFeatureFlags } from "../../../../../feature-flags";

type StatsProps = {
  survey: SurveyEntity;
  stats?: {
    numOfTotalAnswer: number;
    numOfRecent24HoursAnswers: number;
  };
  orgSlug: string;
  groups?: Group[];
  onSurveyUpdate: (survey: SurveyEntity) => void;
  canUpdateSurvey: boolean;
  canReadStats: boolean;
};

export const Stats: React.FC<StatsProps> = ({
  survey,
  stats,
  orgSlug,
  groups,
  onSurveyUpdate,
  canUpdateSurvey,
  canReadStats,
}) => {
  const [name, setName] = useState<string>(survey.name);
  const { auth } = useAuthContextV2();
  const overwriteCustomPathModal = useModal();
  const [pathDuplicateSurveyName, setPathDuplicateSurveyName] =
    useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // TODO(shirakiya): `survey.customPath`はbackendの実装によりnullが入る可能性がある。
  // その影響で<input>要素のvalue attrにnullが入りwarningが出ている。
  const [customPath, setCustomPath] = useState<string | undefined>(
    survey.customPath,
  );

  const [isExcludeFromSearch, setIsExcludeFromSearch] = useState<boolean>(
    survey.isExcludeFromSearch,
  );

  const [isNotifyArrival, setIsNotifyArrival] = useState<boolean>(
    survey.isNotifyArrival,
  );

  useEffect(() => {
    setName(survey.name);
    setCustomPath(survey.customPath);
    setIsExcludeFromSearch(survey.isExcludeFromSearch);
    setIsNotifyArrival(survey.isNotifyArrival);
    // ここ以下のcomponentで管理しているstateをクリアするため、一瞬<Loading />をrenderingする
    // componentを一度クリアしないと、react側で過去のStateを保持してしまうため、Surveyの情報が変更されても最新の情報が描画されない可能性がある
    // なので、componentを強制的に一度クリアして、最新の情報を描画する
    // ref. https://github.com/graffer-inc/cloud-gov/pull/13558
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 200);
  }, [survey]);

  const isDisplayLGWANURL = isLGWANURL(window.location.href);
  const surveyUpdate = useCallback(() => {
    onSurveyUpdate(
      SurveyEntity.fromJSON({
        ...survey,
        name: name,
        customPath: customPath,
        isExcludeFromSearch: isExcludeFromSearch,
        isNotifyArrival: isNotifyArrival,
      }),
    );
  }, [
    survey,
    name,
    customPath,
    isExcludeFromSearch,
    isNotifyArrival,
    onSurveyUpdate,
  ]);

  const onClickUpdateButton = useCallback(async (): Promise<void> => {
    try {
      const { data: pathDuplicateSurvey } =
        await surveyApiClient.fetchSurveyByCustomPath(
          survey.orgId,
          customPath!,
        );
      if (pathDuplicateSurvey.id === survey.id) {
        surveyUpdate();
        return;
      }
      setPathDuplicateSurveyName(pathDuplicateSurvey.name);
      const canUpdate = auth.isServiceProviderAccount()
        ? auth.hasPermissions("surveys.update")
        : auth.checkGroupPermission(pathDuplicateSurvey, ["surveys.update"]);
      if (canUpdate) {
        overwriteCustomPathModal.open();
        return;
      }
      toast.error(
        "入力されたパスは、すでに他のグループによって管理されているアンケートに使用されているため、使用することができません。",
      );
      return;
    } catch (e) {
      if (SmartApplyApiError.is404(e)) {
        surveyUpdate();
        return;
      }
      reportUnexpectedError(e);
      return;
    }
  }, [
    survey.orgId,
    survey.id,
    customPath,
    auth,
    surveyUpdate,
    overwriteCustomPathModal,
  ]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      {canUpdateSurvey && (
        <div className="has-text-right">
          <SecondaryButton
            text={"更新する"}
            disabled={
              !!SurveyEntity.validateName(name) ||
              (!!customPath && !!SurveyEntity.validatePath(customPath))
            }
            onClick={onClickUpdateButton}
          />
        </div>
      )}
      <div className="columns">
        <div className="column is-8">
          <TextField
            label={"アンケート名称"}
            required={true}
            description={"アンケートの名称を入力してください。"}
            placeholder={""}
            errorMessages={SurveyEntity.validateName(name)}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              setName(e.currentTarget.value);
            }}
            value={name}
          />
        </div>
      </div>
      {!!groups && (
        <>
          <div className="is-size-5 mb-2">管理グループ</div>
          <div className="field">
            {groups.length > 0 ? (
              groups.map((v) => <span key={v.id}>{v.name}</span>)
            ) : (
              <span>なし</span>
            )}
          </div>
        </>
      )}
      <div className="columns">
        <div className="column is-8">
          <TextField
            type={"url"}
            label={"アンケートページのパス"}
            description={
              "アンケートのURL内に任意のパスを設定することができます。公開後は編集ができません。"
            }
            placeholder={"例： train-2021"}
            errorMessages={
              !survey.isPublished()
                ? SurveyEntity.validatePath(customPath)
                : undefined
            }
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              setCustomPath(e.currentTarget.value);
            }}
            disabled={survey.isPublished()}
            value={customPath}
          />
        </div>
      </div>
      {survey.isPublished() && (
        <>
          <div className="columns">
            <div className="column is-8">
              <p className="title is-size-6">回答用URL</p>
              <LinkCopier text={survey.answerSurveyURL(orgSlug)} />
            </div>
          </div>
          {/* 2023/11現在、sandboxのLGWAN環境からアンケートのURLを参照することは出来ない */}
          {isDisplayLGWANURL && !StaticFeatureFlags.IS_SANDBOX && (
            <div className="columns">
              <div className="column is-8">
                <p className="title is-size-6 mb-2">回答用URL(LGWAN環境向け)</p>
                <p className="is-size-7 ml-2">
                  ※このURLはLGWAN環境下での閲覧時のみ表示されます。
                </p>
                <LinkCopier text={survey.answerSurveyLGWANURL(orgSlug)} />
              </div>
            </div>
          )}
        </>
      )}
      <div className="columns">
        <div className="column is-8">
          <SurveySearchEngineSetting
            value={isExcludeFromSearch}
            onChange={() => setIsExcludeFromSearch(!isExcludeFromSearch)}
          />
        </div>
      </div>
      <div className="columns">
        <div className="column is-8">
          <SurveyNotifyArrivalSetting
            value={isNotifyArrival}
            onChange={() => setIsNotifyArrival(!isNotifyArrival)}
          />
        </div>
      </div>
      {canReadStats && stats && (
        <div className="columns">
          <div className="column is-8">
            <p className="title is-size-6">統計情報</p>
            <div className="tile is-ancestor">
              <div className="tile is-parent">
                <article className="tile is-child box">
                  <p className="title is-size-5">総回答数</p>
                  {stats.numOfTotalAnswer === 0 ? (
                    <p className="is-size-4 has-text-centered px-4 py-4">
                      まだ回答はありません
                    </p>
                  ) : (
                    <p className="is-size-1 has-text-centered px-4 py-4">
                      {stats.numOfTotalAnswer}
                      <span className="is-size-4">件</span>
                    </p>
                  )}
                </article>
              </div>
              <div className="tile is-parent">
                <article className="tile is-child box">
                  <p className="title is-size-5">24時間以内の回答数</p>
                  {stats.numOfRecent24HoursAnswers === 0 ? (
                    <p className="is-size-4 has-text-centered px-4 py-4">
                      まだ回答はありません
                    </p>
                  ) : (
                    <p className="is-size-1 has-text-centered px-4 py-4">
                      {stats.numOfRecent24HoursAnswers}
                      <span className="is-size-4">件</span>
                    </p>
                  )}
                </article>
              </div>
            </div>
          </div>
        </div>
      )}
      <OverwriteCustomPathModal
        onUpdateSurvey={surveyUpdate}
        updateSurveyName={name}
        pathDuplicateSurveyName={pathDuplicateSurveyName}
        onClose={overwriteCustomPathModal.close}
        isActive={overwriteCustomPathModal.isActive}
      />
    </>
  );
};
